import { useEffect, useState } from "react";
import type { Identity, JmapClient, VacationResponse } from "../lib/jmap";
import { storeTheme, type ThemeChoice } from "../lib/theme";
import type { Settings as Prefs } from "../lib/settings";
import { Icon } from "./Icon";

/**
 * Settings, grouped the way Outlook groups them — Layout, Messages, Compose,
 * Automatic replies — because that is the mental model people arrive with.
 *
 * Everything here changes behaviour somewhere. The temptation with a settings
 * panel is to add switches because the design has room for them; each of these
 * exists because Outlook has it and people use it.
 */

type Update = <K extends keyof Prefs>(key: K, value: Prefs[K]) => void;

/** A labelled row with a control on the right, the drawer's basic unit. */
function Row({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="setrow">
      <div className="setlabel">
        <span>{label}</span>
        {hint && <small>{hint}</small>}
      </div>
      {children}
    </div>
  );
}

function Segmented<T extends string | number>({
  value, options, onChange,
}: {
  value: T;
  options: [T, string][];
  onChange: (v: T) => void;
}) {
  return (
    <div className="seg">
      {options.map(([v, label]) => (
        <button key={String(v)} className={value === v ? "on" : ""} aria-pressed={value === v} onClick={() => onChange(v)}>
          {label}
        </button>
      ))}
    </div>
  );
}

function Toggle({ on, label, onChange }: { on: boolean; label: string; onChange: (v: boolean) => void }) {
  return (
    <button
      className={`sw${on ? " on" : ""}`}
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={() => onChange(!on)}
    />
  );
}

const FIELD: React.CSSProperties = {
  width: "100%", borderRadius: 9, background: "var(--pane2)",
  border: "1px solid var(--line)", color: "var(--tx)",
  font: "400 13px/1.5 Inter", padding: "9px 11px", outline: "none",
};

export function Settings({
  client, identity, prefs, update, onClose, onIdentityChanged,
}: {
  client: JmapClient;
  identity: Identity | null;
  prefs: Prefs;
  update: Update;
  onClose: () => void;
  onIdentityChanged: () => void;
}) {
  const [tab, setTab] = useState<"layout" | "messages" | "compose" | "away">("layout");
  const [name, setName] = useState(identity?.name ?? "");
  const [signature, setSignature] = useState(identity?.textSignature ?? "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Automatic replies live on the server, so they are loaded rather than read
  // from the local preferences.
  const [away, setAway] = useState<VacationResponse | null>(null);
  const [awayLoading, setAwayLoading] = useState(true);

  useEffect(() => {
    if (!identity) return;
    setName((n) => (n === "" ? identity.name : n));
    setSignature((s) => (s === "" ? identity.textSignature : s));
  }, [identity]);

  useEffect(() => {
    let live = true;
    client.vacation()
      .then((v) => live && setAway(v))
      .catch(() => live && setAway(null))
      .finally(() => live && setAwayLoading(false));
    return () => { live = false; };
  }, [client]);

  async function saveIdentity() {
    if (!identity) return;
    setSaving(true);
    setError(null);
    try {
      await client.updateIdentity(identity.id, { name, textSignature: signature });
      setSaved(true);
      onIdentityChanged();
      window.setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save");
    } finally {
      setSaving(false);
    }
  }

  async function saveAway(patch: Partial<VacationResponse>) {
    const next = { ...(away ?? {} as VacationResponse), ...patch };
    setAway(next);
    try {
      await client.setVacation(patch);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save the automatic reply");
    }
  }

  /** A UTCDate for the server, or null, from a date input's value. */
  const asDate = (value: string) => (value ? new Date(`${value}T00:00:00Z`).toISOString() : null);
  const asInput = (value: string | null | undefined) => (value ? value.slice(0, 10) : "");

  return (
    <>
      <div className="scrim on" onClick={onClose} />
      <aside className="drawer on wide" role="dialog" aria-label="Settings" data-screen-label="Settings">
        <h3>
          Settings
          <button className="ibtn x" onClick={onClose} title="Close"><Icon name="x" /></button>
        </h3>

        <div className="settabs" role="tablist">
          {([
            ["layout", "Layout"],
            ["messages", "Messages"],
            ["compose", "Compose"],
            ["away", "Automatic replies"],
          ] as const).map(([id, label]) => (
            <button key={id} role="tab" aria-selected={tab === id} className={tab === id ? "on" : ""} onClick={() => setTab(id)}>
              {label}
            </button>
          ))}
        </div>

        {tab === "layout" && (
          <>
            <div className="set">
              <div className="lb">Appearance</div>
              <Row label="Theme">
                <Segmented<ThemeChoice>
                  value={prefs.theme}
                  options={[["light", "Light"], ["dark", "Dark"], ["system", "System"]]}
                  onChange={(v) => { storeTheme(v); update("theme", v); }}
                />
              </Row>
              <Row label="Reading pane" hint="Where an open message appears">
                <Segmented
                  value={prefs.readingPane}
                  options={[["right", "Right"], ["bottom", "Bottom"], ["hidden", "Off"]]}
                  onChange={(v) => update("readingPane", v)}
                />
              </Row>
              <Row label="Density">
                <Segmented
                  value={prefs.density}
                  options={[["comfortable", "Comfortable"], ["compact", "Compact"]]}
                  onChange={(v) => update("density", v)}
                />
              </Row>
              <Row label="Preview text" hint="Lines of the message shown in the list">
                <Segmented
                  value={prefs.previewLines}
                  options={[[0, "Off"], [1, "1"], [2, "2"], [3, "3"]]}
                  onChange={(v) => update("previewLines", v)}
                />
              </Row>
              <div className="swrow">
                <span>Sender pictures</span>
                <Toggle on={prefs.senderImages} label="Sender pictures" onChange={(v) => update("senderImages", v)} />
              </div>
            </div>

            <div className="set">
              <div className="lb">Date and time</div>
              <Row label="Clock">
                <Segmented
                  value={prefs.hour24 ? 24 : 12}
                  options={[[12, "12-hour"], [24, "24-hour"]]}
                  onChange={(v) => update("hour24", v === 24)}
                />
              </Row>
              <Row label="Week starts on">
                <Segmented
                  value={prefs.weekStart}
                  options={[[0, "Sunday"], [1, "Monday"]]}
                  onChange={(v) => update("weekStart", v)}
                />
              </Row>
            </div>
          </>
        )}

        {tab === "messages" && (
          <>
            <div className="set">
              <div className="lb">Organisation</div>
              <div className="swrow">
                <span>Group into conversations</span>
                <Toggle on={prefs.conversationView} label="Group into conversations" onChange={(v) => update("conversationView", v)} />
              </div>
              <Row label="Message order" hint="Within an open conversation">
                <Segmented
                  value={prefs.newestOnTop ? "top" : "bottom"}
                  options={[["bottom", "Newest last"], ["top", "Newest first"]]}
                  onChange={(v) => update("newestOnTop", v === "top")}
                />
              </Row>
            </div>

            <div className="set">
              <div className="lb">Message handling</div>
              <Row label="Mark as read">
                <Segmented
                  value={prefs.markRead}
                  options={[["immediately", "On open"], ["delay", "After a pause"], ["manual", "Manually"]]}
                  onChange={(v) => update("markRead", v)}
                />
              </Row>
              {prefs.markRead === "delay" && (
                <Row label="After" hint="Seconds a message must stay open">
                  <Segmented
                    value={prefs.markReadSeconds}
                    options={[[2, "2s"], [5, "5s"], [10, "10s"], [30, "30s"]]}
                    onChange={(v) => update("markReadSeconds", v)}
                  />
                </Row>
              )}
              <Row label="After moving or deleting">
                <Segmented
                  value={prefs.afterMove}
                  options={[["list", "Back to list"], ["next", "Open next"]]}
                  onChange={(v) => update("afterMove", v)}
                />
              </Row>
              <div className="swrow">
                <span>Empty Deleted Items on sign out</span>
                <Toggle on={prefs.emptyTrashOnSignOut} label="Empty Deleted Items on sign out" onChange={(v) => update("emptyTrashOnSignOut", v)} />
              </div>
            </div>

            <div className="set">
              <div className="lb">Privacy</div>
              <div className="swrow">
                <span>Always load remote images</span>
                <Toggle on={prefs.alwaysShowImages} label="Always load remote images" onChange={(v) => update("alwaysShowImages", v)} />
              </div>
              <p className="sethint">
                Off by default. A remote image tells the sender you opened the message,
                when, and roughly where from.
              </p>
            </div>
          </>
        )}

        {tab === "compose" && (
          <>
            <div className="set">
              <div className="lb">Sending</div>
              <Row label="Undo send" hint="How long a message waits before it goes">
                <Segmented
                  value={prefs.undoSendSeconds}
                  options={[[0, "Off"], [5, "5s"], [10, "10s"], [20, "20s"]]}
                  onChange={(v) => update("undoSendSeconds", v)}
                />
              </Row>
              <Row label="Reply button does">
                <Segmented
                  value={prefs.replyDefault}
                  options={[["reply", "Reply"], ["replyAll", "Reply all"]]}
                  onChange={(v) => update("replyDefault", v)}
                />
              </Row>
              <div className="swrow">
                <span>Always show Cc and Bcc</span>
                <Toggle on={prefs.alwaysShowCc} label="Always show Cc and Bcc" onChange={(v) => update("alwaysShowCc", v)} />
              </div>
              <div className="swrow">
                <span>Signature on replies</span>
                <Toggle on={prefs.signatureOnReplies} label="Signature on replies" onChange={(v) => update("signatureOnReplies", v)} />
              </div>
            </div>

            <div className="set">
              <div className="lb">Your name</div>
              <input value={name} onChange={(e) => setName(e.target.value)} aria-label="Display name"
                placeholder="Shown as the sender on mail you send" style={FIELD} />
            </div>

            <div className="set">
              <div className="lb">Signature</div>
              <textarea value={signature} onChange={(e) => setSignature(e.target.value)} rows={5}
                aria-label="Signature" placeholder="Appended to messages you send"
                style={{ ...FIELD, resize: "vertical" }} />
              <p className="sethint">
                Separated by the standard <code>-- </code> marker so other clients can fold it away.
              </p>
            </div>

            <button className="pill pri" onClick={() => void saveIdentity()} disabled={saving || !identity}>
              {saving ? "Saving…" : saved ? "Saved" : "Save name and signature"}
            </button>
          </>
        )}

        {tab === "away" && (
          <div className="set">
            <div className="lb">Automatic replies</div>
            {awayLoading ? (
              <p className="sethint">Loading…</p>
            ) : (
              <>
                <div className="swrow">
                  <span>Send automatic replies</span>
                  <Toggle
                    on={!!away?.isEnabled}
                    label="Send automatic replies"
                    onChange={(v) => void saveAway({ isEnabled: v })}
                  />
                </div>
                <p className="sethint">
                  Answers once per sender while it is on. This is stored on the server, so it
                  keeps replying when this browser is closed.
                </p>

                {away?.isEnabled && (
                  <>
                    <div className="lb" style={{ marginTop: 16 }}>Send only during this period</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <input type="date" aria-label="From" style={FIELD}
                        value={asInput(away.fromDate)}
                        onChange={(e) => void saveAway({ fromDate: asDate(e.target.value) })} />
                      <input type="date" aria-label="Until" style={FIELD}
                        value={asInput(away.toDate)}
                        onChange={(e) => void saveAway({ toDate: asDate(e.target.value) })} />
                    </div>
                    <p className="sethint">Leave both empty to reply until you turn this off.</p>

                    <div className="lb" style={{ marginTop: 16 }}>Subject</div>
                    <input style={FIELD} aria-label="Automatic reply subject"
                      value={away.subject ?? ""} placeholder="Out of office"
                      onChange={(e) => setAway({ ...away, subject: e.target.value })}
                      onBlur={(e) => void saveAway({ subject: e.target.value })} />

                    <div className="lb" style={{ marginTop: 16 }}>Message</div>
                    <textarea rows={6} style={{ ...FIELD, resize: "vertical" }} aria-label="Automatic reply message"
                      value={away.textBody ?? ""} placeholder="I am away and will reply when I am back."
                      onChange={(e) => setAway({ ...away, textBody: e.target.value })}
                      onBlur={(e) => void saveAway({ textBody: e.target.value })} />
                  </>
                )}
              </>
            )}
          </div>
        )}

        {error && (
          <div role="alert" style={{ color: "var(--bad)", font: "400 12px/1.4 Inter", marginTop: 10 }}>
            {error}
          </div>
        )}

        <p className="sethint" style={{ marginTop: 18 }}>Signed in as {identity?.email ?? "…"}</p>
      </aside>
    </>
  );
}
